import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ChevronRight, ExternalLink } from 'lucide-react';

import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  SidebarRail,
  SidebarSeparator,
} from '@/components/ui/sidebar';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { navigationConfig } from './navigation-config';
import { ACTIVE_SERVICE, MERCHANT_USER_ID } from '../../api/constants';
import { getMerchantDetails } from '../../api';

// FAM Logo component - uses currentColor for theming
// Based on src/assets/images/fam-logo-black.svg
const FamLogo = ({ className }) => (
  <svg 
    className={className}
    viewBox="0 0 160 40" 
    fill="none" 
    xmlns="http://www.w3.org/2000/svg"
  >
    <path d="M19.4256 10.1714V17.0286H43.4606V27.2H19.4256V40H0V0H46.8354V10.1714H19.4256Z" fill="currentColor"/>
    <path d="M76.975 33.0286H55.0801L51.2114 40H31.4566L56.644 0H75.7404L100.928 40H80.8437L76.975 33.0286ZM71.6248 23.3143L66.0276 13.2571L60.4304 23.3143H71.6248Z" fill="currentColor"/>
    <path d="M142.303 40L142.138 21.7714L129.627 36.4H121.067L108.555 22.4V40H90.8581V0H106.827L125.594 21.3143L143.867 0H159.835L160 40H142.303Z" fill="currentColor"/>
  </svg>
);

/**
 * AppSidebar - Using official shadcn/ui Sidebar component
 * Full-height sidebar with FAM branding and collapsible behavior
 */
const AppSidebar = () => {
  const location = useLocation();
  const [modulePermission, setModulePermission] = useState({
    discount: false,
    payment: false,
  });
  const [payoutMode, setPayoutMode] = useState(null);

  useEffect(() => {
    // Get active service from localStorage
    const activeService = localStorage.getItem(ACTIVE_SERVICE)?.toLowerCase();
    if (activeService) {
      if (activeService === 'both') {
        setModulePermission({ discount: true, payment: true });
      } else {
        setModulePermission((prev) => ({
          ...prev,
          [activeService]: true,
        }));
      }
    }

    // Fetch payout mode
    fetchPayoutMode();
  }, []);

  const fetchPayoutMode = async () => {
    try {
      const merchantUserId = localStorage.getItem(MERCHANT_USER_ID);
      if (merchantUserId) {
        const response = await getMerchantDetails(merchantUserId);
        if (response?.status === 200) {
          setPayoutMode(response?.data?.data?.account_details?.payout_mode);
        }
      }
    } catch (error) {
      console.error('Error fetching merchant details:', error);
    }
  };

  const isActive = (href) => {
    return location.pathname === href || location.pathname.startsWith(href + '/');
  };

  const isParentActive = (item) => {
    if (item.children) {
      return item.children.some((child) => isActive(child.href));
    }
    return isActive(item.href);
  };

  const shouldShowItem = (item) => {
    // Check if item requires direct payout mode
    if (item.requiresDirectPayout && payoutMode !== 'DIRECT_PAYOUT') {
      return false;
    }
    return true;
  };

  const renderNavItem = (item) => {
    if (!shouldShowItem(item)) return null;

    const Icon = item.icon;
    const active = isActive(item.href);
    const hasChildren = item.children && item.children.length > 0;

    // Item with children (collapsible)
    if (hasChildren) {
      const parentActive = isParentActive(item);
      
      return (
        <Collapsible
          key={item.id}
          asChild
          defaultOpen={parentActive}
          className="group/collapsible"
        >
          <SidebarMenuItem>
            <CollapsibleTrigger asChild>
              <SidebarMenuButton tooltip={item.label} isActive={parentActive}>
                {Icon && <Icon />}
                <span>{item.label}</span>
                <ChevronRight className="ml-auto transition-transform duration-200 group-data-[state=open]/collapsible:rotate-90" />
              </SidebarMenuButton>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <SidebarMenuSub>
                {item.children.map((child) => (
                  <SidebarMenuSubItem key={child.id}>
                    <SidebarMenuSubButton asChild isActive={isActive(child.href)}>
                      <Link to={child.href}>
                        <span>{child.label}</span>
                      </Link>
                    </SidebarMenuSubButton>
                  </SidebarMenuSubItem>
                ))}
              </SidebarMenuSub>
            </CollapsibleContent>
          </SidebarMenuItem>
        </Collapsible>
      );
    }

    // Regular nav item
    const isExternalLink = item.href?.startsWith('http');
    
    if (isExternalLink) {
      return (
        <SidebarMenuItem key={item.id}>
          <SidebarMenuButton asChild tooltip={item.label} isActive={active}>
            <a href={item.href} target="_blank" rel="noopener noreferrer">
              {Icon && <Icon />}
              <span>{item.label}</span>
              <ExternalLink className="ml-auto h-3 w-3" />
            </a>
          </SidebarMenuButton>
        </SidebarMenuItem>
      );
    }

    return (
      <SidebarMenuItem key={item.id}>
        <SidebarMenuButton asChild tooltip={item.label} isActive={active}>
          <Link to={item.href}>
            {Icon && <Icon />}
            <span>{item.label}</span>
          </Link>
        </SidebarMenuButton>
      </SidebarMenuItem>
    );
  };

  const renderSection = (section, sectionKey) => {
    // Check if section should be shown based on permissions
    if (sectionKey === 'payment' && !modulePermission.payment) return null;
    if (sectionKey === 'discount' && !modulePermission.discount) return null;

    const visibleItems = section.items.filter(shouldShowItem);
    if (visibleItems.length === 0) return null;

    return (
      <SidebarGroup key={sectionKey}>
        {sectionKey !== 'bottom' && section.label && (
          <SidebarGroupLabel>{section.label}</SidebarGroupLabel>
        )}
        <SidebarGroupContent>
          <SidebarMenu>
            {visibleItems.map((item) => renderNavItem(item))}
          </SidebarMenu>
        </SidebarGroupContent>
      </SidebarGroup>
    );
  };

  return (
    <Sidebar collapsible="icon">
      {/* Sidebar Header - h-12 matches ContentHeader for aligned divider line */}
      {/* Logo hidden when collapsed using group-data attribute */}
      <SidebarHeader className="h-12 !flex-row items-center justify-start border-b px-3 group-data-[collapsible=icon]:hidden">
        <Link to="/home" className="flex items-center">
          <FamLogo className="h-5 w-auto text-foreground" />
        </Link>
      </SidebarHeader>
      
      <SidebarContent>
        {/* Payment section */}
        {renderSection(navigationConfig.payment, 'payment')}

        {/* Discount section - with separator if payment is also shown */}
        {modulePermission.discount && modulePermission.payment && (
          <SidebarSeparator />
        )}
        {renderSection(navigationConfig.discount, 'discount')}
      </SidebarContent>

      {/* Bottom section - Settings, Developer hub */}
      <SidebarFooter>
        <SidebarSeparator />
        <SidebarMenu>
          {navigationConfig.bottom.items.map((item) => renderNavItem(item))}
        </SidebarMenu>
      </SidebarFooter>
      
      {/* Rail for collapse/expand on hover */}
      <SidebarRail />
    </Sidebar>
  );
};

export default AppSidebar;
